import * as React from "react";
import { cn } from "./utils";

/**
 * The colored notification count-bubble look — a small filled `bg-primary`
 * pill that overlays a nav target (a section tab, the rail bell) to mean
 * "N things need you here". Distinct from `<Count>` (a neutral tally next to a
 * heading): this one is an attention signal, so it's the brand color, not a
 * muted tint.
 *
 * Exported as a class string (not a component) because the two call sites wrap
 * different elements — a `<span>` overlay vs. a `<PopoverTrigger>` button — and
 * layer their own context classes (`ring-2 ring-card`, `hover:scale-110`) on
 * top. This is the single source of truth for the bubble's size/shape/color;
 * the two sites used to hand-roll it separately and drifted.
 */
// type-subtext (11/16) + py-px = 18px, the one shared pill height (PRD R6) — same
// rendered height as before, now via tokens instead of off-scale arbitrary
// font-size + line-height literals (the 11px tier is the tokenized type-subtext
// eyebrow size the design audit blesses).
export const COUNT_BUBBLE =
  "inline-flex min-w-[18px] items-center justify-center rounded-full bg-primary px-1.5 py-px type-subtext font-medium tabular-nums text-primary-foreground";

interface CountProps extends Omit<React.ComponentProps<"span">, "children"> {
  value: number | string;
}

/**
 * Small count pill rendered next to a title (e.g. "Installed products 5",
 * "Todo 6"). Filled with a translucent neutral tint (`bg-foreground/10`) so it
 * reads as a distinct bubble against *any* surface in both themes — most of
 * these live inside a `<Card>`, where the old "raised surface-3" fill was
 * invisible in light mode (card `surface-2` and pill `surface-3` are both
 * near-white). The foreground tint always steps away from the local surface:
 * it darkens to light-gray on a light card, lightens to a clear step-up on a
 * dark one.
 *
 * Use anywhere a numeric tally sits next to a heading. For tallies inside
 * prose ("3 tasks across 2 projects"), keep the plain text — Count is for
 * the standalone-pill pattern only.
 */
function Count({ value, className, ...props }: CountProps) {
  return (
    <span
      data-slot="count"
      className={cn(
        // Rides the type-subtext 16px line pair + py-px = 18px, the one shared pill
        // height (PRD R6) — converged with Badge/COUNT_BUBBLE.
        "inline-flex items-center justify-center rounded-md bg-foreground/10 px-1.5 py-px min-w-5 type-subtext tabular-nums text-muted-foreground",
        className,
      )}
      {...props}
    >
      {value}
    </span>
  );
}

/**
 * A right-aligned table count, dimmed at zero. Unlike `Count` (a standalone
 * pill), this is the bare numeric cell that lives inside a data-table column —
 * the column header labels it, so there is no per-cell label. Promoted from
 * verbatim `Num` copies in the shell home + project home pages.
 */
function TableCount({ value }: { value: number }) {
  return (
    <span
      className={cn(
        "tabular-nums type-body",
        value === 0 ? "text-muted-foreground" : "text-foreground",
      )}
    >
      {value}
    </span>
  );
}

export { Count, TableCount };
export type { CountProps };
